import * as React from 'react'
import {Box, Typography, Divider} from '@mui/material'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import GavelIcon from '@mui/icons-material/Gavel'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined'
import {badgeSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, BORDER, SUBTLE, WHITE, PRIMARY_BLUE, withOpacity} from '../../../styles/foundation/colors'

// Same status-tab treatment as weight/list/index.js (lines 68-106): a
// bordered white card of icon + label cells, separated by vertical
// dividers, with an active colour underline and a tinted active background.
const ICONS = {
  action: PendingActionsIcon,
  dispute: GavelIcon,
  resolved: CheckCircleOutlineIcon,
  sku: Inventory2OutlinedIcon,
  analytics: AnalyticsOutlinedIcon,
}

/**
 * `ref` exposes `focusActive()` — the same focusFallbackRef mechanism the
 * Freeze module's rail-based sections already use (see CataloguePage.js/
 * PackingPanel.js) — so a drawer whose own trigger vanished on close (see
 * DisputeDrawer.js's own doc comment) has something sensible to focus
 * instead of letting keyboard focus fall onto <body>.
 */
const TabBar = React.forwardRef(function TabBar({tabs, active, counts, onChange}, ref) {
  const activeRef = React.useRef(null)

  React.useImperativeHandle(ref, () => ({
    focusActive: () => { if (activeRef.current) activeRef.current.focus() },
  }))

  return (
    <Box sx={{bgcolor: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', mb: 2, overflow: 'hidden'}}>
      <Box sx={{display: 'flex', overflowX: 'auto'}}>
        {tabs.map((t, i) => {
          const isActive = active === t.id
          const Icon = ICONS[t.id]
          const count = counts ? counts[t.id] : undefined

          return (
            <React.Fragment key={t.id}>
              {i > 0 && <Divider orientation='vertical' flexItem sx={{borderColor: BORDER}} />}
              <Box
                ref={isActive ? activeRef : undefined}
                tabIndex={isActive ? 0 : undefined}
                onClick={() => onChange(t.id)}
                sx={{
                  // minWidth must be the CONTENT width, not a fixed number. A fixed min-width
                  // replaces flexbox's automatic content-based minimum, so a tab whose label is
                  // longer than it ('SKU Weight Intelligence (289)' needs ~180px) gets squeezed
                  // below its text, and nowrap + visible overflow then spills that text across the
                  // next tab — which is what made the tabs unclickable. With max-content they still
                  // grow to fill a wide row and the row scrolls when there is not enough space.
                  flex: '1 1 auto', minWidth: 'max-content', py: 1.25, px: 2, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                  borderBottom: isActive ? `3px solid ${PRIMARY_BLUE}` : '3px solid transparent',
                  bgcolor: isActive ? withOpacity(PRIMARY_BLUE, 0.06) : 'transparent',
                  transition: 'all 0.15s',
                  '&:hover': {bgcolor: withOpacity(PRIMARY_BLUE, 0.04)},
                }}
              >
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 30, height: 30, borderRadius: '8px', flexShrink: 0,
                  bgcolor: isActive ? withOpacity(PRIMARY_BLUE, 0.12) : withOpacity(PRIMARY_BLUE, 0.07),
                  color: isActive ? PRIMARY_BLUE : SUBTLE,
                  '& .MuiSvgIcon-root': {fontSize: 17},
                }}>
                  {Icon && <Icon />}
                </Box>
                <Typography sx={{
                  fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.semibold,
                  color: isActive ? NAVY : SUBTLE, lineHeight: 1.2, whiteSpace: 'nowrap',
                }}>
                  {t.label}{typeof count === 'number' ? ` (${count})` : ''}
                </Typography>
                {t.badge && (
                  <Box component='span' sx={badgeSx(PRIMARY_BLUE)}>{t.badge}</Box>
                )}
              </Box>
            </React.Fragment>
          )
        })}
      </Box>
    </Box>
  )
})

export default TabBar
